import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DEVICE_NAME_MAX_LENGTH,
  DEVICE_NOTES_MAX_LENGTH,
  type DeviceDetail,
} from '@irrigazione/shared';
import { api } from '../api/client';
import { Notice } from './ui';
import { formatWhen } from '../format';

/**
 * Gestione del dispositivo: rinomina, sospensione e le due cancellazioni.
 *
 * Sta in una scheda a parte perche' non ci si inciampi mentre si guardano i
 * grafici, ed e' ordinata dalla meno alla piu' irreversibile. Le due
 * cancellazioni sono deliberatamente separate: quasi sempre si vuole ripulire lo
 * storico di una sessione di banco, non buttare via configurazione e calibrazioni.
 */
export function ManageTab({ device }: { device: DeviceDetail }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [confirmData, setConfirmData] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [name, setName] = useState(device.name);
  const [notes, setNotes] = useState(device.notes ?? '');

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['device', device.id] });
    queryClient.invalidateQueries({ queryKey: ['devices'] });
    queryClient.invalidateQueries({ queryKey: ['history', device.id] });
  };

  const saveIdentity = useMutation({
    mutationFn: () =>
      api.updateDevice(device.id, { name: name.trim(), notes: notes.trim() || null }),
    onSuccess: refresh,
  });

  const toggleEnabled = useMutation({
    mutationFn: () => api.updateDevice(device.id, { enabled: !device.enabled }),
    onSuccess: refresh,
  });

  const deleteData = useMutation({
    mutationFn: () => api.deleteDeviceData(device.id),
    onSuccess: () => {
      setConfirmData(false);
      refresh();
    },
  });

  const deleteDevice = useMutation({
    mutationFn: () => api.deleteDevice(device.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      queryClient.invalidateQueries({ queryKey: ['gateways'] });
      navigate('/');
    },
  });

  const counts = device.dataCounts;
  const hasData = counts.readings > 0 || counts.events > 0;
  const nameMatches = typedName.trim() === device.name.trim();

  const trimmedName = name.trim();
  const trimmedNotes = notes.trim();
  const nameEmpty = trimmedName.length === 0;
  const changed = trimmedName !== device.name || trimmedNotes !== (device.notes ?? '');
  const canSave = changed && !nameEmpty;

  const revert = () => {
    setName(device.name);
    setNotes(device.notes ?? '');
  };

  return (
    <div className="stack">
      <section className="card stack" style={{ gap: 12 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Nome e note</h3>
        <p className="small secondary" style={{ margin: 0 }}>
          Il nome è quello con cui l&apos;irrigatore compare in dashboard. I controller si
          registrano da soli con un nome provvisorio ricavato dal MAC o dal numero di nodo: qui
          gli dai quello vero.
        </p>

        <label className="field">
          <span>Nome</span>
          <input
            type="text"
            value={name}
            maxLength={DEVICE_NAME_MAX_LENGTH}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && canSave) saveIdentity.mutate();
              if (event.key === 'Escape') revert();
            }}
            placeholder="Nome dell'irrigatore"
            aria-label="Nome dell'irrigatore"
          />
        </label>

        <label className="field">
          <span>
            Note <span className="muted">(facoltative)</span>
          </span>
          <textarea
            value={notes}
            maxLength={DEVICE_NOTES_MAX_LENGTH}
            rows={3}
            onChange={(event) => setNotes(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') revert();
            }}
            placeholder="Dove si trova, a che profondità è la sonda, cosa irriga…"
            aria-label="Note sull'irrigatore"
          />
        </label>
        <p className="tiny muted" style={{ margin: 0 }}>
          Compaiono sotto il nome nella pagina di dettaglio. {trimmedNotes.length}/
          {DEVICE_NOTES_MAX_LENGTH} caratteri.
        </p>

        <div className="row">
          <button
            className="btn btn--primary"
            onClick={() => saveIdentity.mutate()}
            disabled={!canSave || saveIdentity.isPending}
          >
            {saveIdentity.isPending ? 'Salvataggio…' : 'Salva'}
          </button>
          {changed && (
            <button className="btn" onClick={revert}>
              Annulla
            </button>
          )}
          {saveIdentity.isSuccess && !changed && <span className="tiny muted">Salvato</span>}
        </div>

        {nameEmpty && (
          <p className="tiny" style={{ margin: 0, color: 'var(--status-critical)' }}>
            Il nome non può essere vuoto.
          </p>
        )}
        {saveIdentity.error && (
          <Notice variant="error">{(saveIdentity.error as Error).message}</Notice>
        )}
      </section>

      <section className="card stack" style={{ gap: 12 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Sospensione</h3>
        <p className="small secondary" style={{ margin: 0 }}>
          Un irrigatore sospeso continua a riportare lo stato dei sensori ma non irriga più,
          né in automatico né su comando. È l&apos;alternativa reversibile
          all&apos;eliminazione: la configurazione e lo storico restano intatti.
        </p>
        <div className="row">
          <button
            className="btn"
            onClick={() => toggleEnabled.mutate()}
            disabled={toggleEnabled.isPending}
          >
            {device.enabled ? 'Sospendi irrigatore' : 'Riattiva irrigatore'}
          </button>
          <span className="small muted">
            Stato attuale: {device.enabled ? 'attivo' : 'sospeso'}
          </span>
        </div>
        {toggleEnabled.error && (
          <Notice variant="error">{(toggleEnabled.error as Error).message}</Notice>
        )}
      </section>

      <section className="card stack" style={{ gap: 12 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Cancella i dati storici</h3>

        {hasData ? (
          <div className="table-wrap">
            <table>
              <tbody>
                <tr>
                  <td style={{ color: 'var(--text-primary)' }}>Letture dei sensori</td>
                  <td className="num">{counts.readings.toLocaleString('it-IT')}</td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--text-primary)' }}>Irrigazioni registrate</td>
                  <td className="num">{counts.events.toLocaleString('it-IT')}</td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--text-primary)' }}>Comandi</td>
                  <td className="num">{counts.commands.toLocaleString('it-IT')}</td>
                </tr>
                {counts.firstReadingAt && (
                  <tr>
                    <td style={{ color: 'var(--text-primary)' }}>Dato più vecchio</td>
                    <td>{formatWhen(counts.firstReadingAt)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="small muted" style={{ margin: 0 }}>
            Nessun dato storico da cancellare.
          </p>
        )}

        <p className="small secondary" style={{ margin: 0 }}>
          Vengono rimossi letture, irrigazioni e comandi. <strong>Restano</strong> la modalità di
          lavoro, le fasce settimanali e le calibrazioni: l&apos;irrigatore riparte subito a
          registrare, senza doverlo riconfigurare.
        </p>

        {confirmData ? (
          <div className="notice notice--warning stack" style={{ gap: 10 }}>
            <span>
              Cancello {counts.readings.toLocaleString('it-IT')} letture e{' '}
              {counts.events.toLocaleString('it-IT')} irrigazioni? L&apos;operazione non è
              reversibile.
            </span>
            <div className="row">
              <button
                className="btn btn--danger"
                onClick={() => deleteData.mutate()}
                disabled={deleteData.isPending}
              >
                {deleteData.isPending ? 'Cancellazione…' : 'Sì, cancella lo storico'}
              </button>
              <button className="btn" onClick={() => setConfirmData(false)}>
                Annulla
              </button>
            </div>
          </div>
        ) : (
          <div className="row">
            <button
              className="btn btn--danger"
              onClick={() => setConfirmData(true)}
              disabled={!hasData}
            >
              Cancella i dati storici
            </button>
          </div>
        )}

        {deleteData.data && (
          <p className="tiny muted" style={{ margin: 0 }}>
            Rimossi {deleteData.data.removed.readings.toLocaleString('it-IT')} letture e{' '}
            {deleteData.data.removed.events.toLocaleString('it-IT')} irrigazioni.
          </p>
        )}
        {deleteData.error && <Notice variant="error">{(deleteData.error as Error).message}</Notice>}
      </section>

      <section className="card stack" style={{ gap: 12 }}>
        <h3 style={{ margin: 0, fontSize: 15, color: 'var(--status-critical)' }}>
          Elimina l&apos;irrigatore
        </h3>
        <p className="small secondary" style={{ margin: 0 }}>
          Rimuove il dispositivo e tutto ciò che gli appartiene: dati storici, modalità di lavoro,
          fasce settimanali e calibrazioni.
        </p>

        <Notice variant="warning">
          {device.type === 'standalone' ? (
            <span>
              Se il controller è ancora alimentato <strong>ricomparirà da solo</strong>: alla
              telemetria successiva il server non riconoscerà più il suo identificativo, lui si
              registrerà di nuovo e tornerà con le impostazioni di fabbrica. Per toglierlo davvero,
              spegnilo prima. Se ti serve solo fermarlo, usa <em>Sospendi</em> qui sopra.
            </span>
          ) : (
            <span>
              Se il nodo è ancora acceso <strong>ricomparirà da solo</strong>: al primo pacchetto
              ricevuto il gateway lo farà riconoscere e il server lo ricreerà con le impostazioni
              di fabbrica. Per toglierlo davvero, spegnilo prima. Se ti serve solo fermarlo, usa{' '}
              <em>Sospendi</em> qui sopra.
            </span>
          )}
        </Notice>

        {device.irrigating && (
          <Notice variant="error">
            Attenzione: questo irrigatore sta irrigando adesso. Eliminandolo il server perde
            traccia dell&apos;irrigazione in corso, che proseguirà fino alla sua durata massima.
          </Notice>
        )}

        <label className="field">
          <span>
            Per confermare, scrivi il nome dell&apos;irrigatore: <strong>{device.name}</strong>
          </span>
          <input
            type="text"
            value={typedName}
            onChange={(event) => setTypedName(event.target.value)}
            placeholder={device.name}
            autoComplete="off"
          />
        </label>

        <div className="row">
          <button
            className="btn btn--danger"
            onClick={() => deleteDevice.mutate()}
            disabled={!nameMatches || deleteDevice.isPending}
          >
            {deleteDevice.isPending ? 'Eliminazione…' : 'Elimina definitivamente'}
          </button>
          {!nameMatches && typedName.length > 0 && (
            <span className="tiny muted">Il nome non corrisponde</span>
          )}
        </div>
        {deleteDevice.error && (
          <Notice variant="error">{(deleteDevice.error as Error).message}</Notice>
        )}
      </section>
    </div>
  );
}
